import queryString from "query-string";

import { makeHumanReadable } from "@utils";

import type { FilterDescription } from "./FilterModalProps";

/** What `describeQuery` needs to know about a page's filter */
export interface DescribedFilter {
  label: string;
  gt_suffix?: string;
  lt_suffix?: string;
  options?: { label: string; value?: string }[];
}

/**
 * Readable filter names and values for a saved query, e.g. "Practice Status: Active". With `filterFor`, uses the page's
 * filter labels and option labels; without it, humanizes the raw keys and values
 */
export const describeQueryFallback = (
  query: string,
  filterFor?: (key: string) => DescribedFilter | undefined,
): FilterDescription[] =>
  Object.entries(queryString.parse(query)).map(([key, value]) => {
    const parts = key.split(".");
    const values = (Array.isArray(value) ? value : [value]).filter(
      (v): v is string => typeof v === "string" && v !== "",
    );
    const filter = filterFor?.(key);
    const range =
      filter?.gt_suffix && key.endsWith(filter.gt_suffix)
        ? " (from)"
        : filter?.lt_suffix && key.endsWith(filter.lt_suffix)
          ? " (to)"
          : "";
    return {
      key,
      label: `${filter?.label ?? makeHumanReadable(parts[parts.length - 1])}${range}`,
      values: values.map(
        (v) =>
          filter?.options?.find((option) => option.value === v)?.label ??
          makeHumanReadable(v),
      ),
    };
  });
